// --- React ---
import { useEffect, useState } from "react";

// --- Components ---
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

// --- Models ---
import { stationParameterWarningSchema } from '../../../models/schema/stationParameterWarnings';

// --- Utils ---
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { notify } from "../../../common/notificationService";
import { omitMetaFields } from "../../../utils/helper";

// --- Services ---
import stationParameterWarningsSvc from '../../../services/api/stationParameterWarnings';
import FormInput from "../../form-input/FormInput";
import FormMuiSelect from "../../form-input/FormMuiSelect";
import { useTranslation } from "react-i18next";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    data: any | null;
    availableOptions: any[];
    stationId: string | number;
    onSuccess?: () => Promise<void>;
}

const StationParameterWarningsModal = ({
    isOpen,
    onClose,
    data,
    availableOptions,
    stationId,
    onSuccess,
}: Props) => {
    const isCreate = !data?.id;
    const [loading, setLoading] = useState(false);
    const schema = stationParameterWarningSchema;
    const { t } = useTranslation();

    const {
        register,
        handleSubmit,
        reset,
        control,
        watch,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            stationId: Number(stationId),
        },
    });

    console.log('errors:', errors);
    useEffect(() => {
        if (data) {
            reset({
                ...omitMetaFields(data, ['name', 'code', 'unitName']),
                earlyWarningMin: data.earlyWarningMin?.toString(),
                earlyWarningMax: data.earlyWarningMax?.toString(),
            });
        }
    }, [data]);

    const onSubmit = async (values: any) => {
        console.log('values', values);
        setLoading(true);
        try {
            const res = isCreate ? await stationParameterWarningsSvc.createStationParameterWarning({ ...values, stationId: Number(stationId) }) : await stationParameterWarningsSvc.updateStationParameterWarning({ ...values, id: Number(data?.id) });
            if (res && res.content.status) {
                onClose();
                onSuccess?.();
                notify(t("common.success"), isCreate ? t("station.notifications.createSuccess") : t("station.notifications.updateSuccess"), "success");
                setLoading(false);
            }
        } catch (e: any) {
            notify(t("common.error"), t("common.errorOccurred"), "danger");
        } finally {
            setLoading(false);
        }
    };

    const formErrorMessage = (errors as any)['']?.message;

    return (
        <Dialog open={isOpen} onClose={onClose}>
            <DialogTitle>
                {isCreate ? t('station.parameterWarningsModal.title.create') : t('station.parameterWarningsModal.title.update')}
                <hr className="my-2" />
            </DialogTitle>
            <DialogContent>
                <Typography>
                    <div className="min-w-[400px] py-1">
                        {isCreate ? (
                            <div className="flex flex-col gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        {t('station.parameterWarningsModal.fields.parameter')} <span className="text-red-500">*</span>
                                    </label>
                                    <FormMuiSelect
                                        name="parameterId"
                                        control={control}
                                        options={availableOptions}
                                        numberValue
                                    />
                                    {errors?.parameterId && (
                                        <span className="text-red-500 text-xs mt-1">
                                            {errors?.parameterId?.message}
                                        </span>
                                    )}
                                </div>
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    {t('station.parameterWarningsModal.labels.code')} <span className="font-semibold text-gray-700">{availableOptions.find((item) => item.value === watch("parameterId"))?.code}</span>
                                </label>
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    {t('station.parameterWarningsModal.labels.unit')} <span className="font-semibold text-gray-700">{availableOptions.find((item) => item.value === watch("parameterId"))?.unitName}</span>
                                </label>
                                <FormInput
                                    label={t('station.parameterWarningsModal.fields.minValue')}
                                    name="earlyWarningMin"
                                    inputMode="decimal"
                                    register={register("earlyWarningMin")}
                                    error={errors.earlyWarningMin}
                                    required
                                />
                                <FormInput
                                    label={t('station.parameterWarningsModal.fields.maxValue')}
                                    name="earlyWarningMax"
                                    inputMode="decimal"
                                    register={register("earlyWarningMax")}
                                    error={errors.earlyWarningMax}
                                    required
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    {t('station.parameterWarningsModal.labels.parameter')} <span className="font-semibold text-gray-700">{data?.name}</span>
                                </label>
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    {t('station.parameterWarningsModal.labels.code')} <span className="font-semibold text-gray-700">{data?.code}</span>
                                </label>
                                <label className="block text-sm font-medium mb-1 text-gray-700">
                                    {t('station.parameterWarningsModal.labels.unit')} <span className="font-semibold text-gray-700">{data?.unit ?? '-'}</span>
                                </label>
                                <FormInput
                                    label={t('station.parameterWarningsModal.fields.minValue')}
                                    name="earlyWarningMin"
                                    inputMode="decimal"
                                    register={register("earlyWarningMin")}
                                    error={errors.earlyWarningMin}
                                    required
                                />
                                <FormInput
                                    label={t('station.parameterWarningsModal.fields.maxValue')}
                                    name="earlyWarningMax"
                                    inputMode="decimal"
                                    register={register("earlyWarningMax")}
                                    error={errors.earlyWarningMax}
                                    required
                                />
                            </div>
                        )}
                        {formErrorMessage && (
                            <span className="text-red-500 text-xs mt-1 block">
                                {formErrorMessage}
                            </span>
                        )}
                    </div>
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit" variant="contained">
                    {t('station.parameterWarningsModal.buttons.cancel')}
                </Button>
                <Button onClick={handleSubmit(onSubmit)} color="primary" variant="contained">
                    {isCreate ? t('station.parameterWarningsModal.buttons.create') : t('station.parameterWarningsModal.buttons.update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default StationParameterWarningsModal;